import React from 'react';
import classNames from 'classnames';

import { AngleUpIcon, AngleDownIcon } from '../../icons/react';
import { RCIcon } from '../';

interface IProps {
  /** Renders next to the heading text itself. Use sparingly; currently we only use this to display a `RCStarRating. */
  additionalHeadingContent?: React.ReactNode
  /** The content to be shown / hidden on toggle. */
  children: React.ReactNode;
  /** The heading above the toggled area. Should be a title-case phrase without ending punctuation.
   *
   * Keep this to <6 words; it will cleanly wrap to multiple lines if needed but try and minimize the likelihood of wrapping. */
  heading: string;
  /** Passed through to the HTML tag; used to auto-scroll/jump to a specific section and for Aria controls. */
  id: string;
  /** Always display the content. If this is `true` the component won't render a toggle control / indicator and functions like a simple heading. */
  lockOpen?: boolean;
  /** Render as open, initially, but still allow toggling.*/
  defaultOpen?: boolean;
  /** Type inside the toggled area is rendered at `0.9em` (90%) by default; set this to `true` to render type at its usual size.*/
  noResize?: boolean;
  /** Event handler for the toggle. */
  onToggle?: (state: boolean) => void;
  /** Sizes down the heading, toggle & padding. */
  small?: boolean;
}

export function RCToggledContent({
  additionalHeadingContent = undefined,
  children,
  defaultOpen = false,
  heading,
  id,
  lockOpen = false,
  noResize = false,
  onToggle = undefined,
  small = false,
}: IProps) {
  const [collapsed, setCollapsed] = React.useState(!(lockOpen || defaultOpen));

  function toggle() {
    if (lockOpen) return;
    if (onToggle) {
      onToggle(!collapsed);
    }

    setCollapsed(value => !value);
  }

  const classes = classNames(
    'rc-toggled-content',
    { 'rc-toggled-content--collapsed': collapsed,
      'rc-toggled-content--locked': lockOpen,
      'rc-toggled-content--no-resize': noResize,
      'rc-toggled-content--small': small,
    },
  );

  return (
    <div
      className={classes}
      id={`rc-toggled-content__${id}`}
    >
      <h3
        className="rc-toggled-content__heading"
      >
        <button
          type="button"
          onClick={toggle}
          disabled={lockOpen}
          className="rc-toggled-content__heading__button"
          aria-expanded={!collapsed}
          aria-controls={`rc-toggled-content__${id}-content`}
        >
          <span className="rc-toggled-content__heading__title">
            {heading}
          </span>
          {additionalHeadingContent}
          {!lockOpen && (
            <span className="rc-toggled-content__header__icon">
              <RCIcon svgComponent={collapsed ? AngleDownIcon : AngleUpIcon} />
            </span>
          )}
        </button>
      </h3>
      <div className="rc-toggled-content__inner" hidden={collapsed} id={`rc-toggled-content__${id}-content`}>
        {children}
      </div>
    </div>
  );
}
